import { useMemo, useState } from 'react';
import { formatInteger, formatMoney, formatSignedMoney, formatUtcStamp } from '../../shared/format.ts';
import type { BalancePoint, MonitorMarker } from '../../simulation/balance-monitor.ts';

interface TooltipState {
  x: number;
  y: number;
  sequence: number;
  timestampUtc: string | null;
  balance: number;
  pnl: number;
  marker?: MonitorMarker;
}

export function BalanceHistoryChart(props: {
  series: readonly BalancePoint[];
  markers: readonly MonitorMarker[];
  startingBalance: number;
}) {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const width = 920;
  const height = 340;
  const left = 64;
  const right = 16;
  const top = 16;
  const bottom = 36;
  const plotW = width - left - right;
  const plotH = height - top - bottom;

  const layout = useMemo(() => {
    const series = props.series;
    if (series.length === 0) {
      return null;
    }
    const xs = series.map((point) => point.sequence);
    const ys = series.map((point) => point.balance);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(0, props.startingBalance, ...ys);
    const maxY = Math.max(0, props.startingBalance, ...ys);
    const spanX = maxX - minX || 1;
    const spanY = maxY - minY || 1;
    const padY = spanY * 0.08;
    const y0 = minY - padY;
    const y1 = maxY + padY;
    const ySpan = y1 - y0 || 1;
    const xAt = (sequence: number) => left + ((sequence - minX) / spanX) * plotW;
    const yAt = (balance: number) => top + plotH - ((balance - y0) / ySpan) * plotH;
    const peak = series.reduce((best, point) => (point.balance > best.balance ? point : best));
    const lowest = series.reduce((best, point) => (point.balance < best.balance ? point : best));
    const current = series[series.length - 1]!;
    return { minX, maxX, xAt, yAt, peak, lowest, current, y0, y1 };
  }, [plotH, plotW, props.series, props.startingBalance]);

  if (!layout) {
    return <p className="text-sm text-muted">Balance history will appear after the first published report refresh.</p>;
  }

  const { xAt, yAt, peak, lowest, current } = layout;
  const points = props.series.map((point) => `${xAt(point.sequence)},${yAt(point.balance)}`).join(' ');
  const zeroY = yAt(0);
  const startY = yAt(props.startingBalance);

  function nearest(clientX: number, svg: SVGSVGElement): TooltipState | null {
    const rect = svg.getBoundingClientRect();
    const localX = ((clientX - rect.left) / rect.width) * width;
    let best: BalancePoint | null = null;
    let bestDist = Number.POSITIVE_INFINITY;
    for (const point of props.series) {
      const dist = Math.abs(xAt(point.sequence) - localX);
      if (dist < bestDist) {
        bestDist = dist;
        best = point;
      }
    }
    if (!best) {
      return null;
    }
    const marker =
      props.markers.find((row) => row.sequence === best.sequence && row.kind === 'CONFIG') ??
      props.markers.find((row) => row.sequence === best.sequence);
    return {
      x: xAt(best.sequence),
      y: yAt(best.balance),
      sequence: best.sequence,
      timestampUtc: best.timestampUtc,
      balance: best.balance,
      pnl: best.pnl,
      marker,
    };
  }

  return (
    <div className="relative">
      <svg
        role="img"
        aria-label="Balance history from start sequence through the latest processed round"
        viewBox={`0 0 ${width} ${height}`}
        className="w-full"
        onMouseLeave={() => setTooltip(null)}
        onMouseMove={(event) => setTooltip(nearest(event.clientX, event.currentTarget))}
      >
        <title>Balance history</title>
        <line x1={left} y1={zeroY} x2={left + plotW} y2={zeroY} stroke="#d8d4cb" strokeDasharray="4 4" />
        <line x1={left} y1={startY} x2={left + plotW} y2={startY} stroke="#d8d4cb" />
        <text x={left - 8} y={zeroY + 4} textAnchor="end" className="fill-muted" fontSize="11">
          $0
        </text>
        <text x={left - 8} y={startY + 4} textAnchor="end" className="fill-muted" fontSize="11">
          {formatMoney(props.startingBalance)}
        </text>
        <polyline fill="none" stroke="#3f5d56" strokeWidth="2" points={points} />
        {props.markers.map((marker) =>
          marker.kind === 'CONFIG' ? (
            <g key={`config-${marker.sequence}`}>
              <line
                x1={xAt(marker.sequence)}
                y1={top}
                x2={xAt(marker.sequence)}
                y2={top + plotH}
                stroke="#8a8680"
                strokeDasharray="2 4"
                strokeWidth="1"
              />
            </g>
          ) : (
            <circle
              key={`${marker.kind}-${marker.strategyId}-${marker.sequence}`}
              cx={xAt(marker.sequence)}
              cy={yAt(props.series.find((point) => point.sequence === marker.sequence)?.balance ?? current.balance)}
              r={marker.kind === 'STOP' ? 3.5 : 3}
              fill={marker.kind === 'STOP' ? '#8f3d3d' : '#2f5d4a'}
            />
          ),
        )}
        <circle cx={xAt(peak.sequence)} cy={yAt(peak.balance)} r="4.5" fill="#3f5d56" />
        <circle cx={xAt(lowest.sequence)} cy={yAt(lowest.balance)} r="4.5" fill="#8f3d3d" />
        <circle cx={xAt(current.sequence)} cy={yAt(current.balance)} r="5" fill="#1f2430" />
        <text x={left} y={height - 10} className="fill-muted" fontSize="11">
          {formatInteger(layout.minX)}
        </text>
        <text x={left + plotW} y={height - 10} textAnchor="end" className="fill-muted" fontSize="11">
          {formatInteger(layout.maxX)}
        </text>
      </svg>
      {tooltip ? (
        <div
          className="pointer-events-none absolute rounded border border-line bg-panel px-2 py-1.5 text-xs shadow-sm"
          style={{
            left: `min(${(tooltip.x / width) * 100}%, calc(100% - 12rem))`,
            top: Math.max(0, (tooltip.y / height) * 100 - 18) + '%',
          }}
        >
          <p>{formatUtcStamp(tooltip.timestampUtc)}</p>
          <p>Seq {formatInteger(tooltip.sequence)}</p>
          <p>Balance {formatMoney(tooltip.balance)}</p>
          <p>P&L {formatSignedMoney(tooltip.pnl)}</p>
          {tooltip.marker ? (
            <p className="whitespace-pre-line">
              {tooltip.marker.kind === 'CONFIG'
                ? tooltip.marker.detail ?? 'Config changed'
                : `${tooltip.marker.label} ${tooltip.marker.kind} ${formatSignedMoney(tooltip.marker.amount)}`}
            </p>
          ) : null}
        </div>
      ) : null}
      <p className="mt-2 text-xs text-muted">
        Peak {formatMoney(peak.balance)} at {formatInteger(peak.sequence)} · Lowest {formatMoney(lowest.balance)} at{' '}
        {formatInteger(lowest.sequence)} · Current {formatMoney(current.balance)}
      </p>
    </div>
  );
}
